import React, { useState } from "react";
import { Box, Link } from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { colors, fonts, gradients, layout } from "../../theme/tokens";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const COMPANY_ID = process.env.REACT_APP_CONTACT_COMPANY_ID;

// Mirror the backend's validation rules (client-side first pass; the server's
// 400 `fields` array remains the source of truth).
const schema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(200, "Name must be at most 200 characters")
    .required("Full name is required"),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email")
    .required("Email is required"),
  phone: yup.string().max(50, "Phone must be at most 50 characters"),
  subject: yup.string().max(300, "Subject must be at most 300 characters"),
  message: yup
    .string()
    .trim()
    .min(1, "Message is required")
    .max(5000, "Message must be at most 5000 characters")
    .required("Message is required"),
});

// Field names the server can return errors for that map to inputs in this form.
const FORM_FIELDS = ["name", "email", "message", "phone", "subject"];

const labelSx = {
  fontFamily: fonts.body,
  fontSize: "13px",
  fontWeight: 600,
  color: "#3C4D5E",
};

const inputSx = {
  fontFamily: fonts.body,
  p: "13px 16px",
  border: "1px solid #E1E7EE",
  borderRadius: "12px",
  // 16px on mobile prevents iOS Safari from auto-zooming the page on focus.
  fontSize: { xs: "16px", sm: "14.5px" },
  background: "#FAFBFC",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  color: colors.brandNavy,
  "&:focus": { borderColor: colors.steelBlue, background: "#fff" },
  "&::placeholder": { color: colors.subtleText },
};

const Field = ({ label, optional, error, children }) => (
  <Box
    component="label"
    sx={{ display: "flex", flexDirection: "column", gap: "7px" }}
  >
    <Box component="span" sx={labelSx}>
      {label}
      {optional && (
        <Box
          component="span"
          sx={{ color: colors.subtleText, fontWeight: 500 }}
        >
          {" "}
          (optional)
        </Box>
      )}
    </Box>
    {children}
    {error && (
      <Box
        component="span"
        sx={{ fontFamily: fonts.body, fontSize: "12px", color: "#D14343" }}
      >
        {error}
      </Box>
    )}
  </Box>
);

const InfoRow = ({ icon, label, value, href }) => {
  const content = (
    <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
      <Box
        sx={{
          width: "44px",
          height: "44px",
          borderRadius: "12px",
          background: "rgba(255,255,255,.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Box
          sx={{
            fontFamily: fonts.body,
            fontSize: "11.5px",
            color: colors.footerMuted,
            letterSpacing: ".04em",
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            fontFamily: fonts.body,
            fontWeight: 600,
            color: "#fff",
            fontSize: "15px",
          }}
        >
          {value}
        </Box>
      </Box>
    </Box>
  );
  return href ? (
    <Link href={href} underline="none">
      {content}
    </Link>
  ) : (
    content
  );
};

const ContactForm = () => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: "onChange" });

  const onSubmit = async (data) => {
    setSending(true);
    setFormError("");

    // Required fields plus any non-empty optional fields.
    const payload = {
      name: data.name.trim(),
      email: data.email.trim(),
      message: data.message.trim(),
    };
    if (data.phone && data.phone.trim()) payload.phone = data.phone.trim();
    if (data.subject && data.subject.trim())
      payload.subject = data.subject.trim();

    try {
      const res = await fetch(`${API_BASE_URL}/api/contact/${COMPANY_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let body = null;
      try {
        body = await res.json();
      } catch (e) {
        body = null;
      }

      if (res.ok) {
        setSent(true);
        reset();
        try {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {}
        return;
      }

      // Map a 400's per-field errors back onto the inputs.
      if (res.status === 400 && body && Array.isArray(body.fields)) {
        let mappedToField = false;
        body.fields.forEach((f) => {
          if (FORM_FIELDS.includes(f.path)) {
            setError(f.path, { type: "server", message: f.message });
            mappedToField = true;
          }
        });
        // companyId / unmapped validation errors fall back to a general banner.
        if (!mappedToField) {
          setFormError(body.message || "Please check the form and try again.");
        }
        return;
      }

      // 403 / 404 / 429 / 500 — show the server message directly when present.
      setFormError(
        (body && body.message) ||
          "Something went wrong. Please try again or email us directly.",
      );
    } catch (err) {
      setFormError(
        "Something went wrong. Please try again or email us directly.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      component="section"
      sx={{ pt: "30px", pb: "70px", px: { xs: "20px", md: "28px" } }}
    >
      <Box
        sx={{
          maxWidth: layout.maxWidth,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.1fr .9fr" },
          gap: "24px",
          alignItems: "start",
        }}
      >
        {/* Form card */}
        <Box
          sx={{
            background: "#fff",
            border: `1px solid ${colors.lineSoft}`,
            borderRadius: "26px",
            p: { xs: "30px 26px", md: "40px 42px" },
            boxShadow: "0 20px 50px -36px rgba(14,27,42,.5)",
          }}
        >
          {sent ? (
            <Box sx={{ textAlign: "center", py: "40px", px: "10px" }}>
              <Box
                sx={{
                  width: "72px",
                  height: "72px",
                  borderRadius: "50%",
                  background: "rgba(34,165,91,.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                }}
              >
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5 10 17.5 19 7"
                    stroke={colors.success}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
              <Box
                component="h3"
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: "24px",
                  color: colors.navyInk,
                  mt: "22px",
                  mb: 0,
                }}
              >
                Message sent successfully
              </Box>
              <Box
                sx={{
                  fontFamily: fonts.body,
                  fontSize: "15.5px",
                  lineHeight: 1.6,
                  color: colors.mutedText,
                  maxWidth: "380px",
                  mx: "auto",
                  mt: "12px",
                }}
              >
                Thanks, we've received your message. Our team will get back to
                you within 24 to 48 hours.
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setSent(false)}
                sx={{
                  cursor: "pointer",
                  mt: "26px",
                  background: colors.brandNavy,
                  color: "#fff",
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  fontSize: "14px",
                  p: "13px 26px",
                  border: "none",
                  borderRadius: "40px",
                }}
              >
                Send another message
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                component="h2"
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: "24px",
                  color: colors.navyInk,
                  m: 0,
                }}
              >
                Send us a message
              </Box>
              <Box
                sx={{
                  fontFamily: fonts.body,
                  fontSize: "14.5px",
                  color: "#7A8896",
                  mt: "6px",
                }}
              >
                Fill out the form below and we'll be in touch shortly.
              </Box>

              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                sx={{
                  mt: "26px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: "16px",
                  }}
                >
                  <Field label="Full Name" error={errors.name?.message}>
                    <Box
                      component="input"
                      placeholder="John Doe"
                      sx={inputSx}
                      {...register("name")}
                    />
                  </Field>
                  <Field label="Email Address" error={errors.email?.message}>
                    <Box
                      component="input"
                      type="email"
                      placeholder="john@example.com"
                      sx={inputSx}
                      {...register("email")}
                    />
                  </Field>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: "16px",
                  }}
                >
                  <Field label="Phone" optional error={errors.phone?.message}>
                    <Box
                      component="input"
                      placeholder="+234 800 000 0000"
                      sx={inputSx}
                      {...register("phone")}
                    />
                  </Field>
                  <Field
                    label="Subject"
                    optional
                    error={errors.subject?.message}
                  >
                    <Box
                      component="input"
                      placeholder="How can we help?"
                      sx={inputSx}
                      {...register("subject")}
                    />
                  </Field>
                </Box>
                <Field label="Message" error={errors.message?.message}>
                  <Box
                    component="textarea"
                    rows={5}
                    placeholder="Tell us about your project or enquiry..."
                    sx={{ ...inputSx, resize: "vertical", minHeight: "120px" }}
                    {...register("message")}
                  />
                </Field>

                {formError && (
                  <Box
                    sx={{
                      fontFamily: fonts.body,
                      fontSize: "13px",
                      color: "#D14343",
                    }}
                  >
                    {formError}
                  </Box>
                )}

                <Box
                  component="button"
                  type="submit"
                  disabled={sending}
                  sx={{
                    cursor: sending ? "not-allowed" : "pointer",
                    background: gradients.amber,
                    color: "#fff",
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    fontSize: "15px",
                    p: "15px",
                    border: "none",
                    borderRadius: "44px",
                    boxShadow: "0 14px 32px -14px rgba(242,121,28,.7)",
                    mt: "4px",
                    opacity: sending ? 0.75 : 1,
                  }}
                >
                  {sending ? "Sending..." : "Send Message"}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Info panel */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box
            sx={{
              background: gradients.navy,
              borderRadius: "24px",
              p: "34px 32px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "-50px",
                right: "-30px",
                width: "200px",
                height: "200px",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle,rgba(246,166,35,.18),transparent 65%)",
              }}
            />
            <Box
              component="h3"
              sx={{
                fontFamily: fonts.display,
                fontWeight: 700,
                fontSize: "21px",
                color: "#fff",
                position: "relative",
                m: 0,
              }}
            >
              Prefer a direct conversation?
            </Box>
            <Box
              sx={{
                fontFamily: fonts.body,
                fontSize: "14.5px",
                lineHeight: 1.6,
                color: "#A9BACD",
                mt: "10px",
                position: "relative",
              }}
            >
              Give us a call or send an email. Our team responds to all
              enquiries within 24 to 48 hours.
            </Box>
            <Box
              sx={{
                mt: "26px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                position: "relative",
              }}
            >
              <InfoRow
                href="tel:+2348033910386"
                label="PHONE"
                value="+234 803 391 0386"
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6.5 4h-2A1.5 1.5 0 0 0 3 5.6C3 13 11 21 18.4 21A1.5 1.5 0 0 0 20 19.5v-2a1.5 1.5 0 0 0-1.2-1.5l-2.6-.5a1.5 1.5 0 0 0-1.5.7l-.5.8a11 11 0 0 1-4.9-4.9l.8-.5a1.5 1.5 0 0 0 .7-1.5l-.5-2.6A1.5 1.5 0 0 0 6.5 4Z"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                  </svg>
                }
              />
              <InfoRow
                href="mailto:info@argyleconsulting.org"
                label="EMAIL"
                value="info@argyleconsulting.org"
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="5"
                      width="18"
                      height="14"
                      rx="2"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                    <path
                      d="m4 7 8 6 8-6"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                  </svg>
                }
              />
              <InfoRow
                label="LOCATION"
                value="Lagos State, Nigeria"
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                    <circle
                      cx="12"
                      cy="10"
                      r="2.5"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                  </svg>
                }
              />
            </Box>
          </Box>

          {/* Map placeholder */}
          <Box
            sx={{
              borderRadius: "24px",
              overflow: "hidden",
              border: `1px solid ${colors.lineSoft}`,
              position: "relative",
              height: "200px",
              background: "linear-gradient(135deg,#EAF1F8,#F4F7FB)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "linear-gradient(rgba(44,108,168,.12) 1px,transparent 1px),linear-gradient(90deg,rgba(44,108,168,.12) 1px,transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: "46px",
                  height: "46px",
                  borderRadius: "50%",
                  background: gradients.amber,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  boxShadow: "0 10px 24px -8px rgba(242,121,28,.6)",
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z"
                    fill="#fff"
                  />
                  <circle cx="12" cy="10" r="2.5" fill={colors.amber} />
                </svg>
              </Box>
              <Box
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 600,
                  fontSize: "15px",
                  color: colors.brandNavy,
                  mt: "10px",
                }}
              >
                Lagos State, Nigeria
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactForm;
